import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  IndianRupee, Loader2, MapPin, Armchair, ArrowLeft, Wallet, Smartphone,
  CheckCircle2, Copy, Clock, ShieldCheck, Download, MessageCircle, AlertTriangle, ScanLine, Gift, PartyPopper, Eye,
} from "lucide-react";
import { toast } from "sonner";

export default function Massage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("intro"); // intro, details, service, payment, cash, upi, success
  const [form, setForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "" });
  const [service, setService] = useState(null);
  const [order, setOrder] = useState(null);
  const [upi, setUpi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [firstOffer, setFirstOffer] = useState(null);
  const [checking, setChecking] = useState(false);
  const [offers, setOffers] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [addEye, setAddEye] = useState(false);

  useEffect(() => {
    api.get(`/massage/${slug}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    api.get(`/offers/active?chair=${slug}`).then((r) => setOffers(r.data)).catch(() => {});
  }, [slug]);

  const sym = data?.settings?.currency_symbol || "₹";

  const pollOrder = useCallback((oid) => {
    const iv = setInterval(async () => {
      try {
        const { data: o } = await api.get(`/orders/${oid}`);
        if (o.billing_code && o.status === "paid") {
          setOrder(o);
          setStep("success");
          clearInterval(iv);
        }
      } catch (e) {
        console.debug("order poll retry", e?.message);
      }
    }, 3500);
    return iv;
  }, []);

  const checkAndContinue = async () => {
    setChecking(true);
    try {
      const { data: e } = await api.post("/customers/eligibility", { mobile: form.customer_mobile.trim() });
      setFirstOffer(e.first_time_eligible ? e.offer : null);
    } catch (_) {
      setFirstOffer(null);
    } finally {
      setChecking(false);
      setStep("service");
    }
  };

  const createOrder = async (method) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/orders", {
        chair_slug: slug,
        service_id: service.id || null,
        first_time_offer: !!service.first_time,
        coupon_code: coupon.trim() || null,
        eye_massager: !!addEye,
        ...form,
        payment_method: method,
      });
      setOrder(res.order);
      if (method === "upi") {
        setUpi(res.upi);
        setStep("upi");
      } else {
        setStep("cash");
      }
      pollOrder(res.order.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="mx-auto mt-24 max-w-md rounded-2xl bg-card p-8 text-center gold-border">
          <AlertTriangle className="mx-auto text-destructive" size={40} />
          <h2 className="mt-4 font-serif text-xl font-bold">Chair Not Available</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return <Shell><div className="mt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div></Shell>;
  }

  const { chair, services, settings } = data;

  const eyeCfg = {
    enabled: !!settings?.eye_massager_enabled,
    name: settings?.eye_massager_name || "Eye Massager",
    price: settings?.eye_massager_price ?? 50,
    durations: settings?.eye_massager_durations || [20, 30],
  };
  const eyeEligible = !!service && eyeCfg.enabled && eyeCfg.durations.includes(service.duration_minutes) && !service.first_time;
  const baseAmt = service ? (service.effective_price ?? service.price) : 0;
  const eyeAmt = (eyeEligible && addEye) ? eyeCfg.price : 0;
  const totalAmt = baseAmt + eyeAmt;

  return (
    <Shell>
      <div className="mx-auto max-w-md px-4 pb-[calc(4rem+env(safe-area-inset-bottom))]">
        <TestModeBanner mode={settings.payment_mode} />
        <div className="mt-4 rounded-2xl bg-card p-4 gold-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={15} className="gold-text" />{chair.location_name}
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-gold">
              <Armchair size={13} /> {chair.slug}
            </div>
          </div>
        </div>

        {step === "intro" && (
          <div className="mt-5 animate-fade-up rounded-2xl bg-card p-7 text-center gold-border">
            <div className="overline-label">{settings.business_name}</div>
            <h1 className="mt-2 font-serif text-3xl font-bold gold-gradient-text">Smart Massage</h1>
            <p className="mt-1 text-sm text-muted-foreground">Scan. Pay. Relax.</p>
            <div className="mt-6 space-y-2 rounded-xl bg-secondary/50 p-4 text-left text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ScanLine size={15} className="gold-text" /> Choose your massage duration</div>
              <div className="flex items-center gap-2"><Wallet size={15} className="gold-text" /> Pay by Cash or UPI</div>
              <div className="flex items-center gap-2"><ShieldCheck size={15} className="gold-text" /> Get a verified billing code</div>
              <div className="flex items-center gap-2"><Armchair size={15} className="gold-text" /> Show it to staff & relax</div>
            </div>
            <Button className="mt-6 h-12 w-full bg-gold text-base font-semibold text-primary-foreground hover:bg-gold-hover"
              onClick={() => setStep("details")} data-testid="start-flow-btn">
              Get Started
            </Button>
            <p className="mt-4 text-[11px] text-muted-foreground">By continuing you agree to our salon terms. Sessions are non-transferable and single-use.</p>
          </div>
        )}
        {step === "intro" && <PricingDisplay services={services} eye={eyeCfg} sym={sym} />}

        {step === "details" && (
          <div className="mt-5 animate-fade-up rounded-2xl bg-card p-7 gold-border">
            <StepHead onBack={() => setStep("intro")} title="Your Details" step="1 of 3" />
            <div className="mt-5 space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input data-testid="customer-name-input" value={form.customer_name} className="mt-1.5"
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Enter your name" />
              </div>
              <div>
                <Label>Mobile Number *</Label>
                <Input data-testid="customer-mobile-input" value={form.customer_mobile} className="mt-1.5" type="tel"
                  onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} placeholder="10-digit mobile number" />
              </div>
              <div>
                <Label>Email <span className="text-muted-foreground">(optional)</span></Label>
                <Input data-testid="customer-email-input" value={form.customer_email} className="mt-1.5" type="email"
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="you@email.com" />
              </div>
            </div>
            <Button className="mt-6 h-12 w-full bg-gold font-semibold text-primary-foreground hover:bg-gold-hover"
              data-testid="details-continue-btn"
              disabled={checking || !form.customer_name.trim() || form.customer_mobile.trim().length < 10}
              onClick={checkAndContinue}>
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </div>
        )}

        {step === "service" && (
          <div className="mt-5 animate-fade-up rounded-2xl bg-card p-7 gold-border">
            <StepHead onBack={() => setStep("details")} title="Select Massage" step="2 of 3" />
            {offers.length > 0 && (
              <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="offers-banner">
                <div className="flex items-center gap-2 text-emerald-300"><PartyPopper size={16} /><span className="font-semibold">{offers[0].name}</span></div>
                <div className="mt-1 text-xs text-emerald-200/80">{offers[0].discount_percent}% OFF · applied automatically. Valid till {new Date(offers[0].ends_at).toLocaleDateString("en-IN")}.</div>
              </div>
            )}
            <div className="mt-5 space-y-3">
              {firstOffer && (
                <button data-testid="first-time-offer-btn"
                  onClick={() => {
                    const base = services.find((x) => x.duration_minutes === firstOffer.minutes);
                    setService({ id: base?.id || null, first_time: true, duration_minutes: firstOffer.minutes,
                      name: firstOffer.label, effective_price: firstOffer.price, price: firstOffer.price,
                      on_offer: true, original_price: base?.price ?? firstOffer.price });
                    setAddEye(false);
                    setStep("payment");
                  }}
                  className="relative flex w-full items-center justify-between overflow-hidden rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 text-left transition-all hover:border-emerald-400">
                  <div className="absolute right-0 top-0 rounded-bl-lg bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-black">FIRST VISIT ONLY</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Gift size={18} className="text-emerald-400" />
                      <span className="font-serif text-xl font-bold">{firstOffer.minutes} <span className="text-sm text-muted-foreground">min</span></span>
                    </div>
                    <div className="mt-0.5 text-xs text-emerald-300">{firstOffer.label}</div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center text-xl font-bold text-emerald-400"><IndianRupee size={18} />{firstOffer.price}</div>
                    {services.find((x) => x.duration_minutes === firstOffer.minutes) && (
                      <span className="text-xs text-muted-foreground line-through">₹{services.find((x) => x.duration_minutes === firstOffer.minutes).price}</span>
                    )}
                  </div>
                </button>
              )}
              {services.map((s) => (
                <button key={s.id} data-testid={`service-${s.duration_minutes}min-btn`}
                  onClick={() => { setService(s); setAddEye(false); setStep("payment"); }}
                  className="flex w-full items-center justify-between rounded-xl border border-border bg-secondary/40 p-5 text-left transition-all hover:border-gold hover:bg-gold-muted">
                  <div>
                    <div className="font-serif text-2xl font-bold">{s.duration_minutes} <span className="text-sm text-muted-foreground">min</span></div>
                    <div className="text-xs text-muted-foreground">{s.name}</div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center text-xl font-bold gold-text"><IndianRupee size={18} />{s.effective_price ?? s.price}</div>
                    {s.on_offer && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground line-through">₹{s.original_price}</span>
                        <span className="rounded bg-emerald-500/15 px-1.5 text-[9px] font-semibold text-emerald-400">OFFER</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "payment" && (
          <div className="mt-5 animate-fade-up rounded-2xl bg-card p-7 gold-border">
            <StepHead onBack={() => setStep("service")} title="Payment Method" step="3 of 3" />
            <div className="mt-4 rounded-xl bg-secondary/50 p-4 text-center">
              <div className="text-xs text-muted-foreground">{service.duration_minutes} min · {service.name}</div>
              <div className="mt-1 flex items-center justify-center text-3xl font-bold gold-text" data-testid="payment-total"><IndianRupee size={24} />{totalAmt}</div>
              {service.on_offer && !addEye && (
                <div className="mt-1 flex items-center justify-center gap-2 text-xs">
                  <span className="text-muted-foreground line-through">₹{service.original_price}</span>
                  <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 font-semibold text-emerald-400">LIMITED OFFER</span>
                </div>
              )}
              {eyeEligible && addEye && (
                <div className="mx-auto mt-3 max-w-xs space-y-1 border-t border-border pt-2 text-xs">
                  <div className="flex justify-between text-muted-foreground"><span>{service.duration_minutes} min massage</span><span>{sym}{baseAmt}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>{eyeCfg.name} add-on</span><span>+{sym}{eyeCfg.price}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total</span><span className="gold-text">{sym}{totalAmt}</span></div>
                </div>
              )}
            </div>
            {eyeEligible && (
              <button type="button" onClick={() => setAddEye(!addEye)} data-testid="eye-massager-toggle"
                className={`mt-4 flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all ${addEye ? "border-emerald-400 bg-emerald-500/10" : "border-border bg-secondary/40 hover:border-gold"}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: addEye ? "rgba(16,185,129,0.15)" : "rgba(226,184,87,0.08)" }}>
                  <Eye size={20} className={addEye ? "text-emerald-400" : "gold-text"} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Make it more relaxing 👁️</div>
                  <div className="mt-0.5 font-semibold">Add {eyeCfg.name} for just {sym}{eyeCfg.price}</div>
                  <div className="text-xs text-muted-foreground">Optional — relax your eyes during your massage.</div>
                </div>
                <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${addEye ? "border-emerald-400 bg-emerald-400" : "border-muted-foreground/40"}`}>
                  {addEye && <CheckCircle2 size={16} className="text-black" />}
                </div>
              </button>
            )}
            <div className="mt-5 space-y-3">
              {settings.enable_cash && (
                <PayOption icon={Wallet} title="Cash" desc="Pay at the salon counter" testid="pay-cash-btn"
                  onClick={() => createOrder("cash")} busy={busy} />
              )}
              {settings.enable_upi && (
                <PayOption icon={Smartphone} title="GPay / UPI" desc="Google Pay, PhonePe, Paytm & more" testid="pay-upi-btn"
                  onClick={() => createOrder("upi")} busy={busy} />
              )}
              {settings.enable_online && (
                <PayOption icon={ShieldCheck} title="Online Payment" desc="Card / Netbanking (coming soon)" testid="pay-online-btn"
                  onClick={() => toast.info("Online gateway will be enabled soon.")} busy={false} disabled />
              )}
            </div>
            <div className="mt-5 border-t border-border pt-4">
              <Label className="text-xs text-muted-foreground">Have a coupon?</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="FESTIVE30" className="font-mono uppercase" data-testid="coupon-input" />
                <Button variant="outline" className="gold-border text-gold" data-testid="apply-coupon-btn"
                  onClick={async () => {
                    if (!coupon.trim()) return;
                    try {
                      const { data: r } = await api.post("/offers/validate-coupon", { code: coupon.trim(), chair_slug: slug, duration_minutes: service.duration_minutes });
                      setService({ ...service, effective_price: r.pricing.final_price, original_price: r.pricing.original_price, on_offer: r.pricing.on_offer });
                      setCouponMsg({ ok: true, text: `${r.offer.name} applied — ${r.pricing.discount_percent}% off` });
                    } catch (e) { setCouponMsg({ ok: false, text: apiError(e) }); }
                  }}>Apply</Button>
              </div>
              {couponMsg && <div className={`mt-2 text-xs ${couponMsg.ok ? "text-emerald-400" : "text-destructive"}`} data-testid="coupon-msg">{couponMsg.text}</div>}
            </div>
          </div>
        )}

        {step === "cash" && (
          <WaitCard title="Pay at the Counter" icon={Wallet}>
            <p className="text-center text-sm text-muted-foreground">
              Please pay <b className="gold-text">{sym}{service.effective_price ?? service.price}</b> at the salon counter.
            </p>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              After payment, ask the staff member to <b>confirm your cash payment</b>. Your billing code will appear here automatically.
            </p>
            <PendingSpinner />
          </WaitCard>
        )}

        {step === "upi" && upi && (
          <WaitCard title="Scan & Pay via UPI" icon={Smartphone}>
            <div className="mb-3 rounded-lg bg-secondary/50 py-2 text-center text-sm">
              <span className="text-muted-foreground">Paying at </span><b className="gold-text">{upi.location_name}</b>
            </div>
            <div className="mx-auto w-fit rounded-xl bg-white p-3">
              <img src={upi.qr} alt="UPI QR" className="h-52 w-52" data-testid="upi-qr" />
            </div>
            <div className="mt-4 text-center">
              <div className="text-xs text-muted-foreground">Paying to</div>
              <div className="font-semibold">{upi.upi_name}</div>
              <button onClick={() => { navigator.clipboard.writeText(upi.upi_id); toast.success("UPI ID copied"); }}
                className="mx-auto mt-1 flex items-center gap-1 font-mono text-xs text-gold" data-testid="copy-upi-btn">
                {upi.upi_id} <Copy size={11} />
              </button>
              <div className="mt-2 flex items-center justify-center text-2xl font-bold gold-text"><IndianRupee size={20} />{upi.amount}</div>
            </div>
            {!upi.static && (
              <a href={upi.intent}>
                <Button className="mt-4 h-11 w-full bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="open-upi-app-btn">
                  Open UPI App
                </Button>
              </a>
            )}
            {upi.instructions && <p className="mt-3 whitespace-pre-line text-center text-[11px] text-muted-foreground">{upi.instructions}</p>}
            <p className="mt-3 text-center text-xs text-muted-foreground">
              After paying, ask staff to confirm your payment. Your billing code appears here automatically once verified.
            </p>
            <PendingSpinner />
          </WaitCard>
        )}

        {step === "success" && order && (
          <SuccessCard order={order} settings={settings} sym={sym} />
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border py-3">
        <div className="mx-auto flex max-w-md items-center justify-center px-4"><Logo size="sm" /></div>
      </div>
      {children}
    </div>
  );
}

function TestModeBanner({ mode }) {
  if (mode !== "TEST") return null;
  return (
    <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-400" data-testid="test-mode-banner">
      <AlertTriangle size={13} /> TEST MODE — payments are confirmed by staff. Live gateway not yet active.
    </div>
  );
}

function StepHead({ onBack, title, step }) {
  return (
    <div className="flex items-center justify-between">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold" data-testid="step-back-btn">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="text-right">
        <div className="font-serif text-lg font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground">Step {step}</div>
      </div>
    </div>
  );
}

function PayOption({ icon: Icon, title, desc, onClick, busy, testid, disabled }) {
  return (
    <button onClick={onClick} disabled={busy || disabled} data-testid={testid}
      className="flex w-full items-center gap-4 rounded-xl border border-border bg-secondary/40 p-4 text-left transition-all hover:border-gold hover:bg-gold-muted disabled:opacity-50">
      <div className="flex h-11 w-11 items-center justify-center rounded-full gold-border" style={{ background: "rgba(226,184,87,0.08)" }}>
        <Icon className="gold-text" size={20} />
      </div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      {busy ? <Loader2 className="h-4 w-4 animate-spin gold-text" /> : null}
    </button>
  );
}

function WaitCard({ title, icon: Icon, children }) {
  return (
    <div className="mt-5 animate-fade-up rounded-2xl bg-card p-7 gold-border">
      <div className="mb-5 flex flex-col items-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full gold-border" style={{ background: "rgba(226,184,87,0.08)" }}>
          <Icon className="gold-text" size={26} />
        </div>
        <h2 className="mt-3 font-serif text-xl font-bold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function PendingSpinner() {
  return (
    <div className="mt-5 flex items-center justify-center gap-2 rounded-lg bg-secondary/50 py-3 text-sm text-muted-foreground" data-testid="payment-pending">
      <Loader2 className="h-4 w-4 animate-spin gold-text" /> Waiting for payment confirmation…
    </div>
  );
}

function SuccessCard({ order, settings, sym }) {
  const copy = () => {
    navigator.clipboard.writeText(order.billing_code);
    toast.success("Billing code copied");
  };
  const waText = encodeURIComponent(
    `King's Professional Salon – Smart Massage\nBilling Code: ${order.billing_code}\nService: ${order.duration_minutes} min\nAmount: ${sym}${order.amount}\nChair: ${order.chair_code}`
  );
  const digits = (order.customer_mobile || "").replace(/\D/g, "");
  const waNumber = digits.length === 10 ? `91${digits}` : digits;
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : `https://wa.me/?text=${waText}`;
  return (
    <div className="mt-5 animate-fade-up">
      <div className="print-area rounded-2xl bg-card p-7 gold-border">
        <div className="flex flex-col items-center">
          <CheckCircle2 className="text-emerald-400" size={52} data-testid="payment-success-icon" />
          <h2 className="mt-3 font-serif text-2xl font-bold">Payment Successful</h2>
          <p className="text-sm text-muted-foreground">Show this code to staff to start your massage</p>
        </div>
        <div className="mt-6 rounded-xl bg-gold-muted p-5 text-center gold-border">
          <div className="overline-label">Your Billing Code</div>
          <div className="mt-2 font-mono text-3xl font-bold tracking-wider gold-text" data-testid="billing-code-display">{order.billing_code}</div>
          <button onClick={copy} className="mx-auto mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-gold no-print" data-testid="copy-code-btn">
            <Copy size={12} /> Copy code
          </button>
        </div>
        {order.bonus_billing_code && (
          <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-center" data-testid="bonus-code-block">
            <div className="flex items-center justify-center gap-1.5 text-emerald-300"><Gift size={15} /> <span className="text-xs font-semibold uppercase tracking-wider">Buy One Get One — Bonus Free Session</span></div>
            <div className="mt-2 font-mono text-2xl font-bold tracking-wider text-emerald-400" data-testid="bonus-code-display">{order.bonus_billing_code}</div>
            <div className="mt-1 text-[11px] text-emerald-200/80">Show this second code to staff for your free session.</div>
          </div>
        )}
        <div className="mt-5 space-y-2.5 text-sm">
          <Row l="Customer" v={order.customer_name} />
          <Row l="Service" v={`${order.duration_minutes} min · ${order.service_name}`} />
          <Row l="Amount" v={`${sym}${order.amount}`} />
          <Row l="Eye Massager" v={order.eye_massager_selected ? `Yes · +${sym}${order.eye_massager_price}` : "No"} />
          <Row l="Payment" v={order.payment_method === "cash" ? "Cash" : "UPI"} />
          <Row l="Chair" v={order.chair_code} />
          <Row l="Location" v={order.location_name} />
          <Row l="Date" v={new Date(order.confirmed_at || order.created_at).toLocaleString("en-IN")} />
        </div>
        <div className="mt-5 rounded-lg bg-secondary/50 p-3 text-center text-xs text-muted-foreground">
          {settings.receipt_footer}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 no-print">
        <Button variant="outline" className="gap-2 gold-border text-gold hover:bg-gold-muted" onClick={() => window.print()} data-testid="download-receipt-btn">
          <Download size={16} /> Receipt
        </Button>
        <a href={waHref} target="_blank" rel="noreferrer">
          <Button variant="outline" className="w-full gap-2 gold-border text-gold hover:bg-gold-muted" data-testid="whatsapp-receipt-btn">
            <MessageCircle size={16} /> WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between border-b border-border/60 pb-2">
      <span className="text-muted-foreground">{l}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function PricingDisplay({ services, eye, sym }) {
  const priceFor = (d) => {
    const s = services.find((x) => x.duration_minutes === d);
    return s ? (s.effective_price ?? s.price) : 0;
  };
  return (
    <div className="mt-5 animate-fade-up rounded-2xl bg-card p-6 gold-border" data-testid="chair-pricing-display">
      <div className="text-center overline-label">Smart Massage</div>
      <div className="mt-4 space-y-2">
        {services.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-lg bg-secondary/40 px-4 py-2.5">
            <span className="font-semibold">{s.duration_minutes} MIN</span>
            <span className="flex items-center gold-text font-bold"><IndianRupee size={14} />{s.effective_price ?? s.price}</span>
          </div>
        ))}
      </div>
      {eye.enabled && (
        <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-center gap-2 text-emerald-300"><Eye size={16} /><span className="text-[10px] font-bold uppercase tracking-wider">Optional Add-On</span></div>
          <div className="mt-1 font-semibold">{eye.name} <span className="text-emerald-400">+{sym}{eye.price}</span></div>
          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
            {(eye.durations || []).map((d) => (
              <div key={d} className="flex justify-between"><span>{d} min + {eye.name}</span><span className="font-medium text-foreground">{sym}{priceFor(d) + eye.price}</span></div>
            ))}
            <div className="pt-1 text-muted-foreground/70">Available with {(eye.durations || []).join(" & ")} minute sessions. Not available on 10 min.</div>
          </div>
        </div>
      )}
    </div>
  );
}
